import getContactList, {
  type BeneficiaryContactType,
} from 'api/v2/transactions/getContactList';
import useQueryClient from 'hooks/react-query/useClientQuery';

import { useCallback, useState } from 'react';
import type { Paging } from 'types/pagination.types';
import {
  useBeneficiaryVerification,
  VerifyBeneficiaryStatus,
} from 'components/pages/Banks/Wallet/MoneyTransfer/hooks/useBeneficiaryVerification/useBeneficiaryVerification';
import type { VerifyBeneficiaryResponse } from 'api/v2/transactions/verifyBeneficiary';
import { FIVE_MINUTES } from 'utils/time';

export interface ContactSearchResult {
  paging: Paging;
  data: Array<
    BeneficiaryContactType & {
      label: string;
      value: string;
      verificationData?: VerifyBeneficiaryResponse;
    }
  >;
}

export interface UseContactSearchReturn {
  searchByName: (name?: string) => Promise<ContactSearchResult>;
  searchByIban: (iban?: string) => Promise<ContactSearchResult>;
  setCurrentContact: (contact: BeneficiaryContactType | null) => void;
  verifyContact: (
    name: string,
    iban: string,
  ) => Promise<VerifyBeneficiaryResponse>;
  getCurrentContactVerification: () => VerifyBeneficiaryResponse | null;
}

/**
 * Custom hook for searching contacts by name or IBAN using React Query
 * Provides caching, loading states, and error handling
 */
export const useContactSearch = (): UseContactSearchReturn => {
  const queryClient = useQueryClient();
  const [currentContact, setCurrentContact] =
    useState<BeneficiaryContactType | null>(null);
  const [currentVerification, setCurrentVerification] =
    useState<VerifyBeneficiaryResponse | null>(null);

  const { verifyBeneficiary } = useBeneficiaryVerification({
    onSuccess: (data) => {
      setCurrentVerification(data);
    },
    onError: (error) => {
      console.error('Beneficiary verification failed:', error);
      setCurrentVerification(null);
    },
  });

  const createEmptyResponse = (): ContactSearchResult => ({
    paging: {
      page: 1,
      pageCount: 0,
      pagesCount: 0,
      perPage: 10,
      totalCount: 0,
    },
    data: [],
  });

  const searchByName = useCallback(
    async (name?: string): Promise<ContactSearchResult> => {
      if (!name) {
        return createEmptyResponse();
      }

      // Skip API call if searching for the same contact that's already selected
      if (currentContact?.name === name) {
        return createEmptyResponse();
      }

      const queryKey = ['contactSearch', 'name', name];

      // Try to get cached data first
      const cachedData =
        queryClient.getQueryData<ContactSearchResult>(queryKey);
      if (cachedData) {
        return cachedData;
      }

      // Fetch and cache the data using React Query
      const result = await queryClient.fetchQuery({
        queryKey,
        queryFn: async () => {
          const apiResult = await getContactList({ name });

          // For each contact, try to get verification data if available
          const dataWithVerification = await Promise.all(
            apiResult.items.map(async (item) => {
              try {
                // Check if we have cached verification data
                const verificationKey = [
                  'beneficiaryVerification',
                  item.name,
                  item.iban,
                ];
                const cachedVerification =
                  queryClient.getQueryData<VerifyBeneficiaryResponse>(
                    verificationKey,
                  );

                return {
                  ...item,
                  label: item.name,
                  value: item.name,
                  verificationData: cachedVerification,
                };
              } catch (error) {
                // If verification fails, still return the contact without verification data
                return {
                  ...item,
                  label: item.name,
                  value: item.name,
                };
              }
            }),
          );

          return {
            ...apiResult,
            data: dataWithVerification,
          };
        },
        staleTime: FIVE_MINUTES, // 5 minutes
        gcTime: FIVE_MINUTES * 2, // 10 minutes
      });

      return result;
    },
    [currentContact?.name, queryClient],
  );

  const searchByIban = useCallback(
    async (iban?: string): Promise<ContactSearchResult> => {
      if (!iban) {
        return createEmptyResponse();
      }

      // Skip API call if searching for the same contact that's already selected
      if (currentContact?.iban === iban) {
        return createEmptyResponse();
      }

      const queryKey = ['contactSearch', 'iban', iban];

      // Try to get cached data first
      const cachedData =
        queryClient.getQueryData<ContactSearchResult>(queryKey);
      if (cachedData) {
        return cachedData;
      }

      // Fetch and cache the data using React Query
      const result = await queryClient.fetchQuery({
        queryKey,
        queryFn: async () => {
          const apiResult = await getContactList({ iban });

          // For each contact, try to get verification data if available
          const dataWithVerification = await Promise.all(
            apiResult.items.map(async (item) => {
              try {
                // Check if we have cached verification data
                const verificationKey = [
                  'beneficiaryVerification',
                  item.name,
                  item.iban,
                ];
                const cachedVerification =
                  queryClient.getQueryData<VerifyBeneficiaryResponse>(
                    verificationKey,
                  );

                return {
                  ...item,
                  label: item.iban,
                  value: item.iban,
                  verificationData: cachedVerification,
                };
              } catch (error) {
                // If verification fails, still return the contact without verification data
                return {
                  ...item,
                  label: item.iban,
                  value: item.iban,
                };
              }
            }),
          );

          return {
            ...apiResult,
            data: dataWithVerification,
          };
        },
        staleTime: FIVE_MINUTES,
        gcTime: FIVE_MINUTES * 2,
      });

      return result;
    },
    [currentContact?.iban, queryClient],
  );

  const verifyContact = useCallback(
    async (name: string, iban: string): Promise<VerifyBeneficiaryResponse> => {
      const result = await verifyBeneficiary({ name, iban });
      return result;
    },
    [verifyBeneficiary],
  );

  const getCurrentContactVerification =
    useCallback((): VerifyBeneficiaryResponse | null => {
      return currentVerification;
    }, [currentVerification]);

  const handleSetCurrentContact = useCallback(
    (contact: BeneficiaryContactType | null) => {
      setCurrentContact(contact);
      // Reset verification when contact changes
      if (!contact) {
        setCurrentVerification(null);
      }
    },
    [],
  );

  return {
    searchByName,
    searchByIban,
    setCurrentContact: handleSetCurrentContact,
    verifyContact,
    getCurrentContactVerification,
  };
};
